import { ParsedResumeContent } from "../Interface/Index";
import apiClient from "./api-client.service";
import axios from "axios";
import { Userprofile } from "../Entities/user-profile.entity";

class ProfileMatcherService {
  private APIBaseUrl: string = "";

  private static instance: ProfileMatcherService;

  constructor() {
    if (ProfileMatcherService.instance) {
      return ProfileMatcherService.instance;
    }
    this.APIBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
    ProfileMatcherService.instance = this;
  }

  async getResumeUploadUrl(fileName: string, fileType: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/profile-matcher/upload?filename=${fileName}&fileType=${fileType}`
      );
      const data = response.data;
      return data;
    } catch (error) {
      console.log("Error while creating url", error);
    }
  }

  async uploadResume(url: string, file: File) {
    try {
      const response = await axios.put(url, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      if (response.status === 200) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.log("Error while uploading resume", error);
    }
  }

  // Method to get presigned upload url and upload resume to s3
  async uploadResumeToS3AndMatchProfile(file: File, applicationId: string) {
    try {
      const uploadData = await this.getResumeUploadUrl(file.name, file.type);

      if (!uploadData)
        throw new Error("Error while getting presigned upload url");

      const uploadFileResponse = await this.uploadResume(uploadData.url, file);

      if (!uploadFileResponse) {
        throw new Error("Error while uploading resume to s3");
      }

      return uploadData.fileName;
    } catch (error: any) {
      throw new Error("Error while uploading resume to s3");
    }
  }

  // Method to match resume against a job description (new user profile)
  async matchResumeAgainstJobDescriptionByCreatingNewUserProfile(
    jobApplicationId: string,
    jobPostId: string,
    fileName: string,
    parsedResumeData: ParsedResumeContent
  ) {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/profile-matcher/match`,
        {
          jobApplicationId,
          jobPostId,
          fileName,
          parsedResume: parsedResumeData,
        }
      );
      const data = response.data;
      return data;
    } catch (error: any) {
      console.error(
        "Error while matching resume against job description by creating new user profile",
        error
      );
      return;
    }
  }

  // Method to match resume against a job description (existing user profile)
  async matchResumeAgainstJobDescriptionByUsingExistingUserProfile(
    jobApplicationId: string,
    jobPostId: string,
    profileId: string
  ) {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/profile-matcher/match/existing`,
        {
          jobApplicationId,
          jobPostId,
          profileId,
        }
      );
      const data = response.data;
      return data;
    } catch (error: any) {
      console.error(
        "Error while matching resume against job description by using existing user profile",
        error
      );
      return;
    }
  }

  // Method to get match result by job application id
  async getMatchResultByJobApplicationId(applicationId: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/profile-matcher/match/${applicationId}`
      );
      const data = response.data;
      return data;
    } catch (error) {
      console.error("Error while fetching match result", error);
    }
  }

  // Method to get all uploaded profiles
  async getAllUploadedProfiles(isDirectUpload?: boolean) {
    try {
      const params = isDirectUpload !== undefined ? { isDirectUpload: isDirectUpload.toString() } : {};
      const response = await apiClient.get(`${this.APIBaseUrl}/users/profile`, { params });
      const data = response.data;
      return data;
    } catch (error) {
      console.error("Error while fetching all uploaded profiles", error);
    }
  }

  // Method to upload resume to s3 and save user profile (direct uploaded file)
  async saveUserResume(resume: File, parsedResumeData: ParsedResumeContent) {
    try {
      const formData = new FormData();
      formData.append("file", resume);
      formData.append("parsedResume", JSON.stringify(parsedResumeData));
      const response = await apiClient.post(
        `${this.APIBaseUrl}/profile-matcher/save`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (err: any) {
      console.error("UserService: Error while saving user resume", err);
      throw err;
    }
  }

  // Method to update user profile by profile id
  async updateUserProfile(profileId: string, data: Partial<Userprofile>): Promise<{ success: boolean; message: string; data: Userprofile | null }> {
    try {
      const response = await apiClient.put(
        `${this.APIBaseUrl}/profile-matcher/user-profile/${profileId}`,
        data
      );
      return response.data;
    } catch (err: any) {
      console.error("ProfileMatcherService: Error while updating user profile", err);
      throw err;
    }
  }

  // Method to get user profile by profile id
  async getUserProfileById(profileId: string): Promise<Userprofile | null> {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/profile-matcher/user-profile/${profileId}`
      );
      return response.data;
    } catch (err: any) {
      console.error("UserService: Error while fetching user profile by id", err);
      return null;
    }
  }

  // Method to get formatted resume by resume raw data (parse API)
  async getFormattedResumeByResumeRawData(file: File): Promise<ParsedResumeContent | null> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await apiClient.post(
        `${this.APIBaseUrl}/profile-matcher/parse`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "Error while getting formatted resume by resume raw data",
        error
      );
      return null;
    }
  }
}

export default ProfileMatcherService;
